/**
 * Room-Level E2E Encryption
 *
 * All room messages are encrypted using a symmetric key derived from the room secret.
 */

import { hkdf } from '@noble/hashes/hkdf'
import { sha256 } from '@noble/hashes/sha256'
import { base64ToUint8Array, uint8ArrayToBase64 } from './identity'

const ENCRYPTION_INFO = new TextEncoder().encode('crowdtalkie-room-encryption-v1')
const SALT_PREFIX = new TextEncoder().encode('crowdtalkie-room-salt-')
const PIN_SEPARATOR = new TextEncoder().encode(':pin:')

/**
 * Derive a symmetric encryption key from the room secret
 * Uses roomId as salt for defense-in-depth against rainbow table attacks
 * If PIN is provided, it's included in the key material for cryptographic enforcement
 *
 * @param roomSecret - The room's secret (base64 encoded)
 * @param roomId - The room ID (used as salt)
 * @param pin - Optional PIN that cryptographically protects the room
 */
export async function deriveRoomKey(
  roomSecret: string,
  roomId?: string,
  pin?: string
): Promise<CryptoKey> {
  // Use HKDF to derive key material from the room secret
  const secretBytes = base64ToUint8Array(roomSecret)

  // If PIN is provided, combine it with the secret
  // This cryptographically enforces PIN - wrong PIN = wrong key = can't decrypt
  let inputKeyMaterial: Uint8Array
  if (pin) {
    const pinBytes = new TextEncoder().encode(pin)
    inputKeyMaterial = new Uint8Array(secretBytes.length + PIN_SEPARATOR.length + pinBytes.length)
    inputKeyMaterial.set(secretBytes, 0)
    inputKeyMaterial.set(PIN_SEPARATOR, secretBytes.length)
    inputKeyMaterial.set(pinBytes, secretBytes.length + PIN_SEPARATOR.length)
    console.log('[RoomEncryption] Deriving key with PIN protection')
  } else {
    inputKeyMaterial = secretBytes
  }

  // Create salt from roomId for domain separation
  // If no roomId provided (backward compat), use a fixed salt
  const saltInput = roomId
    ? new TextEncoder().encode(roomId)
    : new TextEncoder().encode('default-room-salt')
  const salt = new Uint8Array(SALT_PREFIX.length + saltInput.length)
  salt.set(SALT_PREFIX)
  salt.set(saltInput, SALT_PREFIX.length)

  const keyMaterial = hkdf(sha256, inputKeyMaterial, salt, ENCRYPTION_INFO, 32)

  // Import as AES-GCM key - create copy to ensure proper ArrayBuffer
  return await crypto.subtle.importKey(
    'raw',
    new Uint8Array(keyMaterial).buffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Generate a new room secret
 */
export function generateRoomSecret(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return uint8ArrayToBase64(bytes)
}

/**
 * Encrypt data for room broadcast
 */
export async function encryptForRoom(
  data: Uint8Array,
  roomKey: CryptoKey
): Promise<Uint8Array> {
  // Generate random IV
  const iv = new Uint8Array(12)
  crypto.getRandomValues(iv)

  // Encrypt - create copy to ensure proper ArrayBuffer
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    roomKey,
    new Uint8Array(data).buffer
  )

  // Combine IV + ciphertext
  const result = new Uint8Array(iv.length + encrypted.byteLength)
  result.set(iv)
  result.set(new Uint8Array(encrypted), iv.length)

  return result
}

/**
 * Decrypt data from room
 */
export async function decryptFromRoom(
  data: Uint8Array,
  roomKey: CryptoKey
): Promise<Uint8Array> {
  // Extract IV and ciphertext
  const iv = data.slice(0, 12)
  const ciphertext = data.slice(12)

  // Decrypt
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    roomKey,
    ciphertext
  )

  return new Uint8Array(decrypted)
}

/**
 * Encrypt a string message
 */
export async function encryptMessage(
  message: string,
  roomKey: CryptoKey
): Promise<string> {
  const data = new TextEncoder().encode(message)
  const encrypted = await encryptForRoom(data, roomKey)
  return uint8ArrayToBase64(encrypted)
}

/**
 * Decrypt a string message
 */
export async function decryptMessage(
  encryptedMessage: string,
  roomKey: CryptoKey
): Promise<string> {
  const data = base64ToUint8Array(encryptedMessage)
  const decrypted = await decryptFromRoom(data, roomKey)
  return new TextDecoder().decode(decrypted)
}

/**
 * Parse room join link (secure version - no secret in URL)
 * Link format: https://crowdtalkie.com/join/{roomId}
 * Secret is shared separately for security
 */
export function parseRoomLink(url: string): { roomId: string; roomSecret?: string } | null {
  try {
    const parsed = new URL(url)
    const pathParts = parsed.pathname.split('/')
    const joinIndex = pathParts.indexOf('join')

    if (joinIndex === -1 || joinIndex >= pathParts.length - 1) {
      return null
    }

    const roomId = pathParts[joinIndex + 1]
    // Legacy support: check if secret is in hash (old format)
    const roomSecret = parsed.hash ? parsed.hash.slice(1) : undefined

    if (!roomId) {
      return null
    }

    return { roomId, roomSecret }
  } catch {
    return null
  }
}

/**
 * Create a join link for a room (secure version - no secret in URL)
 * Secret should be shared via separate secure channel
 *
 * @deprecated Use createSecureShareData for sharing room access
 */
export function createRoomLink(roomId: string, roomSecret: string): string {
  // For backward compatibility, still include secret in hash
  // TODO: Remove secret from URL in future version
  return `https://crowdtalkie.com/join/${roomId}#${roomSecret}`
}

/**
 * Create secure share data (preferred method)
 * Returns room access info that should be shared via secure channel
 */
export function createSecureShareData(
  roomId: string,
  roomSecret: string,
  roomName: string,
  pin?: string
): {
  roomId: string
  roomSecret: string
  roomName: string
  pin?: string
  joinUrl: string
  qrData: string
} {
  // Join URL without secret (secure)
  const joinUrl = `https://crowdtalkie.com/join/${roomId}`

  // QR data is a URL so any phone camera can scan and open it directly
  // The room data is base64-encoded in the 'd' parameter
  const roomData = JSON.stringify({
    v: 1, // version
    r: roomId,
    s: roomSecret,
    n: roomName,
    ...(pin && { p: pin }),
  })
  // Use URL-safe base64 encoding
  const encodedData = btoa(roomData).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  const qrData = `https://crowdtalkie.com/join/${roomId}?d=${encodedData}`

  console.log('[createSecureShareData] Generated:', {
    roomId,
    roomName,
    qrDataUrl: qrData.substring(0, 80) + '...',
  })

  return {
    roomId,
    roomSecret,
    roomName,
    pin,
    joinUrl,
    qrData,
  }
}

/**
 * Decode URL-safe base64
 */
function decodeUrlSafeBase64(str: string): string {
  // Add padding back
  let padded = str.replace(/-/g, '+').replace(/_/g, '/')
  while (padded.length % 4) {
    padded += '='
  }
  return atob(padded)
}

/**
 * Valid hierarchy types for rooms
 */
const VALID_HIERARCHY_TYPES = ['standalone', 'command', 'sector'] as const
type HierarchyType = typeof VALID_HIERARCHY_TYPES[number]

/**
 * Validate and return a hierarchy type, or undefined if invalid
 */
function validateHierarchyType(value: string | null | undefined): HierarchyType | undefined {
  if (value && VALID_HIERARCHY_TYPES.includes(value as HierarchyType)) {
    return value as HierarchyType
  }
  return undefined
}

/**
 * Parse QR data from secure share
 * Supports both:
 * - New URL format: https://crowdtalkie.com/join/ROOMID?d=BASE64_DATA
 * - Legacy JSON format: {"v":1,"r":"...","s":"...","n":"..."}
 * - Sector format with commandRoomId and hierarchyType
 */
export function parseSecureShareQR(qrData: string): {
  roomId: string
  roomSecret: string
  roomName: string
  pin?: string
  commandRoomId?: string
  hierarchyType?: 'standalone' | 'command' | 'sector'
} | null {
  try {
    // Try URL format first (new format)
    if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
      const url = new URL(qrData)
      const encodedData = url.searchParams.get('d')
      // Get hierarchy params from URL query string (sector joins)
      const cmdParam = url.searchParams.get('cmd')
      const hierarchyTypeParam = validateHierarchyType(url.searchParams.get('hierarchyType'))

      if (encodedData) {
        const jsonStr = decodeUrlSafeBase64(encodedData)
        const data = JSON.parse(jsonStr)
        if (data.v === 1 && data.r && data.s && data.n) {
          return {
            roomId: data.r,
            roomSecret: data.s,
            roomName: data.n,
            pin: data.p,
            // Hierarchy data can come from URL params or encoded data
            commandRoomId: cmdParam || data.commandRoomId,
            hierarchyType: hierarchyTypeParam || validateHierarchyType(data.hierarchyType),
          }
        }
      }
      return null
    }

    // Fall back to legacy JSON format (also supports sector data)
    const data = JSON.parse(qrData)
    if (data.v === 1 && data.r && data.s && data.n) {
      return {
        roomId: data.r,
        roomSecret: data.s,
        roomName: data.n,
        pin: data.p,
        commandRoomId: data.commandRoomId,
        hierarchyType: validateHierarchyType(data.hierarchyType),
      }
    }
    // Handle legacy JSON format without version (sector-only case)
    // Security: require roomSecret to prevent unauthorized access
    if (data.roomId && data.roomSecret && data.commandRoomId && data.hierarchyType === 'sector') {
      const safeRoomName = typeof data.roomId === 'string' && data.roomId.length > 0
        ? `Sector ${data.roomId.substring(0, 8)}`
        : 'Unknown Sector'
      return {
        roomId: data.roomId,
        roomSecret: data.roomSecret,
        roomName: data.roomName || safeRoomName,
        commandRoomId: data.commandRoomId,
        hierarchyType: 'sector',
      }
    }
    return null
  } catch {
    return null
  }
}
